import { api } from "@/lib/http.ts";
import type {
  PushSubscriptionStatusReq,
  PushSubscriptionStatusResp,
} from "@/types/pushSubscriptions.ts";
import * as Sentry from "@sentry/react";
import { queryOptions, skipToken } from "@tanstack/react-query";

/**
 * Whether the signed-in account is registered on this browser's push endpoint.
 *
 * The server answers, not the browser: a browser has one push subscription
 * however many accounts are signed in on it, so holding one says nothing about
 * this account. `endpoint` is null while the browser has no subscription, and
 * then there is nothing to ask.
 */
export const pushSubscriptionQueries = {
  all: () => ["pushSubscription"] as const,
  status: (endpoint: string | null) =>
    queryOptions({
      queryKey: [...pushSubscriptionQueries.all(), "status", endpoint] as const,
      queryFn: endpoint
        ? async ({ signal }) => {
            try {
              const response = await api.post<
                PushSubscriptionStatusResp,
                PushSubscriptionStatusReq
              >("/api/a/subscriptions/status", { endpoint }, { signal });
              return response.data.subscribed;
            } catch (error) {
              Sentry.captureException(error);
              throw error;
            }
          }
        : skipToken,
      // The answer only changes through this app's own on/off, which writes
      // the cache directly, so there is nothing to refetch for.
      staleTime: Number.POSITIVE_INFINITY,
    }),
};
